import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { LabelTableService } from '@client/app/services/label-table/label-table.service';
import { InverseColorCodePipe } from '@client/app/filters/inverse-color-code.pipe';
import { Subscription } from 'rxjs';

@Component({
    selector: 'label-table',
    standalone: false,
    template: `
        <div class="label-table d-flex">
            <span role="button"
                  tabindex="0"
                  [attr.aria-label]="'Toggle label text' | translate"
                  class="d-flex flex-wrap align-items-center mr-auto label-btn"
                  (click)="toggleTxtVisibility($event)"
                  (keydown)="toggleTxtVisibility($event)">
                <ng-container *ngFor="let label of assignedLabels; index as index; trackBy: trackByIndex">
                    <span
                            [ngbTooltip]="label.name"
                            class="label text-ellipsis"
                            [ngStyle]="getColorStyles(label)"
                    >
                    <span *ngIf="showTxt" class="d-flex align-items-center">
                        <span *ngIf="label.excluded" aria-hidden="true" class="fas fa-ban mr-1"></span>
                        <span>{{label.name}}</span>
                    </span>
                </span>
                </ng-container>
            </span>
            <span
                role="button"
                tabindex="0"
                *ngIf="canEdit()"
                class="btn btn-xs btn-label-edit align-self-start"
                [ngClass]="{'no-labels': assignedLabels.length === 0}"
                [attr.aria-label]="'Edit Labels' | translate"
                (click)="toggleEdit($event)"
                (keydown)="toggleEdit($event)">
                <span class="fas fa-tag" aria-hidden="true"></span>
                <label-container-selector [allLabels]="existingLabels"
                                      [(monShow)]="showLabelSelector"
                                      (onUpdateLabel)="onUpdateLabel.emit()"
                                      (getLabels)="getLabels.emit()"
                                      [currentLabels]="assignedLabels">
                </label-container-selector>
            </span>
        </div>
    `,
})
export class LabelTableComponent implements OnInit, OnDestroy {
    @Input() existingLabels: any[] = [];
    @Input() assignedLabels: any[] = [];
    @Input() noLabelSelector?: boolean = false;

    @Output() getLabels = new EventEmitter();
    @Output() onUpdateLabel = new EventEmitter<void>();

    public showTxt = false;
    public showLabelSelector = false;
    private subscription?: Subscription;

    constructor (
        private labelTableService: LabelTableService,
        private inverseColorCodePipe: InverseColorCodePipe,
    ) {}

    ngOnInit (): void {
        this.noLabelSelector = this.noLabelSelector || false;
        this.showTxt = false;
        this.showLabelSelector = false;
        this.subscription = this.labelTableService.labelTxtVisibility.subscribe((visibility: boolean) => {
            this.showTxt = visibility;
        });
    }

    ngOnDestroy (): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    toggleTxtVisibility (event: any): void {
        if (
            event.type === 'click' ||
            event.key === 'Enter' ||
            event.key === ' '
        ) {
            event.preventDefault();
            event.stopImmediatePropagation();
            if (this.showTxt) {
                this.labelTableService.hideLabelTxt();
            } else {
                this.labelTableService.showLabelTxt();
            }
        }
    }

    getColorStyles (label: any): { [key: string]: string } {
        let backgroundString = label.color;
        if (label.excluded && !this.showTxt) {
            backgroundString = 'repeating-linear-gradient(to right, ' + label.color + ', ' + label.color + ' 4px' + ', white 4px, white 6px)';
        }
        return {
            'background': backgroundString,
            color: this.inverseColorCodePipe.transform(label.color),
        };
    }

    canEdit (): boolean {
        return !this.noLabelSelector && this.labelTableService.canAssignLabels;
    }

    toggleEdit (event: any): void {
        if (
            event.type === 'click' ||
            event.key === 'Enter' ||
            event.key === ' '
        ) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.showLabelSelector = !this.showLabelSelector;
        }
    }

    trackByIndex (index: number, item: any): number {
        return index;
    }
}
